import React, { useState, useEffect } from 'react';
import { ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid';
import folderDocumentShareService from '../services/FolderDocumentShareService';
import Button from '../shared/Button';
import Dialog from '../shared/Dialog';
import { showToast } from '../shared/Toast';
import VerifyOtp from '../submission_boards/recipient/VerifyOtp';
import logo from '../../../assets/images/filmik-horizontal-logo-black.svg'; 

interface Props {
  keyToken: string;
  project: any;
  itemName: string;
  itemType: string;
  recipientEmail: string;
  requiresOtp: boolean;
}

interface BreadcrumbItem {
  name: string;
  uuid: string;
}

const DOCUMENT_PLACEHOLDER = '/assets/icons/document-placeholder.svg';

export default function FolderDocumentShareLanding({
  keyToken,
  project,
  itemName,
  itemType,
  recipientEmail,
  requiresOtp = true,
}: Props) {
  const [step, setStep] = useState<'request' | 'otp' | 'content'>(requiresOtp ? 'request' : 'content');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [contents, setContents] = useState<any[]>([]);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  
  // Navigation state
  const [currentFolderUuid, setCurrentFolderUuid] = useState<string | null>(null);
  const [displayItemName, setDisplayItemName] = useState(itemName);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  
  // File Preview state
  const [selectedFile, setSelectedFile] = useState<any | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    if (step === 'content') {
      fetchContents(currentFolderUuid);
    }
  }, [step, currentFolderUuid]);

  const handleRequestOtp = async () => {
    setIsSendingOtp(true);
    try {
      const response = await folderDocumentShareService.getOtp(project.uuid, keyToken);
      if (response.success) {
        setStep('otp');
        showToast('Verification code sent to your email.', 'success');
      }
    } catch (error) {
      showToast('Failed to send verification code. Please try again.', 'danger');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleValidateOtp = async (otpToValidate: string) => {
    setIsValidating(true);
    try {
      const response = await folderDocumentShareService.validateOtp(project.uuid, keyToken, otpToValidate);
      if (response.otp_valid) {
        setStep('content');
      } else {
        throw new Error('Invalid OTP');
      }
    } catch (error: any) {
      throw error; // Re-throw so VerifyOtp can handle error state
    } finally {
      setIsValidating(false);
    }
  };

  const fetchContents = async (folderUuid: string | null = null) => {
    setIsLoadingContent(true);
    try {
      const response = await folderDocumentShareService.getFolderContents(project.uuid, keyToken, folderUuid || undefined);
      if (response.success) {
        setContents(response.contents || []);
        if (response.item_name) {
          setDisplayItemName(response.item_name);
        }
        if (response.breadcrumbs) {
          setBreadcrumbs(response.breadcrumbs);
        }
      }
    } catch (error: any) {
      const message = error.response?.data?.error || 'Failed to load shared content.';
      showToast(message, 'danger');
      console.error(error);
    } finally {
      setIsLoadingContent(false);
    }
  };

  const handleFolderClick = (e: React.MouseEvent, uuid: string) => {
    e.preventDefault();
    setCurrentFolderUuid(uuid);
    setSelectedFile(null);
  };

  const handleFileClick = (e: React.MouseEvent, file: any) => {
    e.preventDefault();
    setSelectedFile(file);
  };

  if(step === 'request') {
    return (
      <div className="w-full mx-auto  bg-gray-100 min-h-screen">
        <>
          <div className="w-full flex flex-col ">
            <div className="w-full flex-1">
              <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 px-6 sm:px-0">
                <div className="flex flex-col py-10 px-14 gap-2-1 bg-white rounded-lg shadow-xl w-full max-w-md"
                style={{
                  boxShadow:'0px 2px 16px 0px #00000040'
                }}>
                  <div className="flex justify-center mb-6">
                    <img src={logo} alt="Filmik Logo" className="h-8"/>
                  </div>
        
                  <h1 className="text-2xl font-semibold text-center text-gray-800 mb-6">
                    Access Shared {itemType}
                  </h1>
                   
                  <p className="text-center text-sm text-gray-500 mb-6 px-4">
                    This {itemType.toLowerCase()} has been shared with <strong>{recipientEmail}</strong>.
                    Click below to receive a secure verification code.
                  </p>
                  <button
                      type="button"
                      disabled={isSendingOtp}
                    onClick={handleRequestOtp}
                      className={`
                          w-full mt-8 py-3 rounded-lg font-semibold text-white
                          ${
                            !isSendingOtp
                              ? 'bg-blue-600 hover:bg-blue-700'
                              : 'bg-blue-400 cursor-not-allowed'
                          }
                          transition-colors duration-200
                        `}
                      >
                      {isSendingOtp ? 'Sending code...' : 'Send Verification Code'}
                    </button>                 
                </div>
              </div>
            </div>
          </div>
        </>
    </div>      
    );
  }

  if (step === 'otp') {
    return (
      <VerifyOtp
        method="email"
        contactInfo={recipientEmail}
        onSuccess={handleValidateOtp}
        onResend={handleRequestOtp}
      />
    );
  }

  const breadcrumbPages = [...breadcrumbs];
  if (selectedFile) {
    breadcrumbPages.push({ name: selectedFile.name || selectedFile.formatted_name, uuid: 'current-file' });
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumbs Section */}
      <div className="hidden lg:block">
        <hr className="border-gray-200" />
        <nav className="flex border-b justify-start py-3 border-gray-200 bg-white" aria-label="Breadcrumb">
          <ol role="list" className="flex items-center space-x-4 px-4 sm:px-6 lg:px-8">
            {breadcrumbPages.map((crumb, index) => (
              <li key={crumb.uuid}>
                <div className="flex items-center">
                  {index !== 0 && (
                    <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  )}
                  {index === breadcrumbPages.length - 1 ? (
                    <span className="ml-4 text-sm font-medium text-gray-900 cursor-default">
                      {crumb.name}
                    </span>
                  ) : (
                    <button
                      onClick={(e) => handleFolderClick(e, crumb.uuid)}
                      className={`${index !== 0 ? 'ml-4' : ''} text-sm font-medium text-gray-500 hover:text-gray-700`}
                    >
                      {crumb.name}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </nav>
      </div>

      <main className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className={`${selectedFile ? 'w-full' : 'max-w-6xl'} mx-auto`}>
          {selectedFile ? (
            <div className="w-full">
              <hr className="mt-4 mb-4 lg:mb-8" />
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 flex min-h-screen">
                {/* Left Column: Preview Area (8 cols) */}
                <div 
                  className="lg:col-span-8 flex justify-center items-center bg-black relative"
                  style={{ minHeight: '25rem' }}
                >
                  {selectedFile.is_image ? (
                    <img
                      src={selectedFile.access_url}
                      className="max-w-full max-h-full object-contain"
                      alt={selectedFile.name}
                    />
                  ) : selectedFile.is_video ? (
                    <video
                      src={selectedFile.access_url}
                      controls
                      className="max-w-full max-h-full"
                      style={{ width: '100% !important', height: 'auto !important' }}
                    />
                  ) : selectedFile.is_pdf ? (
                    <iframe
                      src={selectedFile.access_url}
                      width="100%"
                      height="100%"
                      className="w-full h-full"
                      style={{ minHeight: '37.5rem' }}
                      title="PDF Preview"
                    />
                  ) : (
                    <div className="text-center text-white p-12">
                      <div className="bg-gray-800 rounded-2xl p-8 mb-6 inline-block">
                        <img src={DOCUMENT_PLACEHOLDER} className="w-24 h-24 mx-auto opacity-50" alt="File Icon" />
                      </div>
                      <h4 className="text-xl font-bold mb-4">No Preview Available</h4>
                      <p className="text-gray-400 mb-8 max-w-sm">
                        This file type cannot be previewed directly. You can download it to view the content.
                      </p>
                      <a
                        href={selectedFile.access_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-bold transition-all shadow-lg shadow-blue-900/20"
                      >
                        Download File
                      </a>
                    </div>
                  )}
                </div>

                {/* Right Column: Notes Area (4 cols) */}
                {/* <div className="lg:flex lg:col-span-4 flex-col">
                  <div className="lg:static lg:block lg:flex lg:flex-1 lg:flex-col">
                    <div className="inline-block align-bottom w-full bg-white rounded-lg px-0 pt-0 pb-0 text-left overflow-hidden shadow-xl lg:max-w-none lg:p-0 lg:my-0 lg:flex lg:flex-1 lg:flex-col h-full">
                      <div className="lg:flex lg:flex-1 lg:flex-col">
                        <div className="text-left pt-4 pr-4 pl-4 lg:pr-10 lg:pl-10 lg:text-center lg:flex lg:flex-col lg:flex-none">
                          <div role="none">
                            <p className="text-lg" role="none">Notes</p>
                          </div>
                          <hr className="mt-2" />
                        </div>
                        <div className="mt-2 lg:flex lg:flex-col h-full">
                          <div className="h-full flex flex-col justify-center align-middle items-center">
                            <div className="flex pr-4 pl-4 lg:pr-10 lg:pl-10 text-center">
                              <span className="text-center text-base text-gray-400 leading-6 font-normal">
                                You need to be logged in to view notes for this document.
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <a
                  href={selectedFile.access_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="text-sm font-semibold text-blue-600 hover:text-blue-700 p-2"
                >
                  Full Resolution Download
                </a>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-3xl leading-9 font-medium text-gray-900 p-2">{displayItemName}</span>
              </div>

              <hr className="mt-4 mb-4 lg:mb-8" />

              {isLoadingContent ? (
                <div className="flex flex-col justify-center items-center h-64 gap-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
                  <p className="text-gray-500 font-medium animate-pulse">Loading contents...</p>
                </div>
              ) : contents.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 xl:gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {contents.map((item, index) => {
                    const isFolder = !!item.project_folder_type;

                    if (isFolder) {
                      // Folder Item - Horizontal Layout
                      return (
                        <div key={index} className="col-span-1">
                          <div className="bg-white shadow rounded-lg hover:shadow-md transition-shadow">
                            <div className="w-full px-4 py-5 sm:p-4 flex items-center justify-between">
                              <button
                                onClick={(e) => handleFolderClick(e, item.uuid)}
                                className="flex items-center w-full text-left"
                              >
                                <span className="inline-block w-8 lg:w-16 lg:h-16 mr-4 lg:p-4 lg:rounded-full lg:bg-blue-50 flex items-center justify-center">
                                  {item.is_episode || item.is_block ? (
                                    <span className="text-lg lg:text-2xl text-blue-500">
                                      {item.is_episode ? item.episode_number : item.block_number}
                                    </span>
                                  ) : (
                                    <img
                                      src={item.thumbnail_url}
                                      className="w-full h-full object-contain"
                                      alt="Folder Icon"
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                        (e.target as HTMLImageElement).parentElement!.innerHTML = `
                                          <svg class="w-full text-blue-200" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                          </svg>
                                        `;
                                      }}
                                    />
                                  )}
                                </span>
                                <span className="block text-gray-900 text-sm font-medium truncate">
                                  {item.name || item.formatted_name}
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      // Document Item - Vertical Card Layout
                      return (
                        <div key={index} className="col-span-1 flex">
                          <div className="w-full bg-white rounded-lg shadow p-4 group transition-all hover:shadow-lg">
                            <div className="h-full lg:h-auto w-full flex lg:flex-col gap-3 lg:gap-0 justify-between lg:justify-center items-center">
                              {/* Thumbnail Area */}
                              <a
                                href={item.access_url || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => handleFileClick(e, item)}
                                className="w-16 lg:w-full lg:h-56 lg:mb-4 overflow-hidden flex flex-none items-center justify-center rounded lg:rounded-lg bg-gray-50 group-hover:bg-blue-50 transition-colors"
                              >
                                {item.thumbnail_url ? (
                                  <img
                                    src={item.thumbnail_url}
                                    className="w-full h-full object-cover rounded lg:rounded-lg"
                                    alt={item.name}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = DOCUMENT_PLACEHOLDER;
                                      (e.target as HTMLImageElement).className = 'w-10 lg:w-24 opacity-40';
                                    }}
                                  />
                                ) : (
                                  <img
                                    src={DOCUMENT_PLACEHOLDER}
                                    className="w-10 lg:w-24 opacity-40 group-hover:opacity-60 transition-opacity"
                                    alt="File"
                                  />
                                )}
                              </a>

                              {/* Info Area */}
                              <div className="lg:w-full flex flex-grow justify-between gap-3 lg:mt-auto h-full overflow-hidden">
                                <div className="flex items-center gap-2 overflow-hidden w-full">
                                  <span className="block text-gray-900 text-sm font-medium truncate group-hover:text-blue-600 transition-colors">
                                    {item.name || item.formatted_name}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  })}
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-20 text-center flex flex-col items-center">
                  <div className="bg-gray-50 p-6 rounded-full mb-6">
                    <svg className="h-16 w-16 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0a2 2 0 01-2 2H6a2 2 0 01-2-2m16 0l-2.586 2.586a2 2 0 01-2.828 0L6 13m16 0l-2.586 2.586a2 2 0 01-2.828 0L6 13" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Contents Available</h3>
                  <p className="text-gray-500 max-w-sm mx-auto">
                    This folder is currently empty or you don't have permission to view its sub-items.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
